const uniq = (value, index, self) => self.indexOf(value) === index;

export const edgesToNodes = (resource, type) => {
  if (!resource || !type) return [];
  return resource.edges.map(({ node }) => {
    switch (type) {
      case 'cartline':
        return formatCartLine(node);
      case 'image':
        return formatImage(node);
      case 'media':
        return formatMedia(node);
      case 'product':
        return formatProduct(node);
      case 'variant':
        return formatVariant(node);
      default:
        return null;
    }
  });
};

export const formatBundle = (prods) => {
  const bundleProducts = edgesToNodes(prods, 'product');
  const products = bundleProducts;

  const activeVariants = products.map((p) =>
    p.variants.find((v) => v.availableForSale),
  );

  const bundleValue = products.reduce((total, product) => {
    const totalPrice =
      total + parseFloat(product.priceRange.minVariantPrice.amount);
    return totalPrice;
  }, 0);

  return {
    products,
    initialProducts: bundleProducts,
    activeVariants,
    available: activeVariants.every((v) => v && v.availableForSale),
    value: {
      amount: bundleValue,
      currencyCode: window.TMW.localization.currencyCode,
    },
  };
};

export const formatQuickViewBundle = (prods) => {
  const bundleProducts = edgesToNodes(prods, 'product');
  const products = bundleProducts;

  const activeVariants = products.map((p) =>
    p.variants.find((v) => v.availableForSale),
  );
  return {
    products,
    available: activeVariants.every((v) => v && v.availableForSale),
  };
};

export const formatCart = (cart) => {
  const subtotal = cart?.cost?.subtotalAmount || {
    amount: 0,
    currencyCode: window.TMW.localization.currencyCode,
  };
  return {
    ...cart,
    subtotal,
    lines: formatCartLines(edgesToNodes(cart?.lines, 'cartline')),
  };
};

export const formatCartLine = (line) => ({
  ...line,
});

export const formatCartLines = (lines) => {
  if (!lines) return [];

  const processedBundles = [];
  const formattedLines = lines
    .map((line) => {
      const bundleProductsId = line.attributes?.find(
        (attr) => attr.key === '_bundleProductsId',
      );
      // If not a bundle item return the default line object
      if (!bundleProductsId) return formatCartLine(line);
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId.value)) return null;

      // Get bundle items
      const bundleItems = lines
        .filter((l) => {
          const currentId = l.attributes.find(
            (attr) => attr.key === '_bundleProductsId',
          )?.value;
          if (currentId === bundleProductsId.value) return l;
          return null;
        })
        .map((l) => l);

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId.value);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(
        firstItem.attributes.find((a) => a.key === '_bundleData').value,
      );

      // Return bundle line item
      return {
        merchandise: bundleData.merchandise,
        cost: bundleData.cost,
        quantity: firstItem.quantity,
        children: bundleItems,
        id: line.id,
      };
    }, [])
    .filter((i) => i);

  return formattedLines.filter((x) => x);
};

export const formatCartLinesAjax = (lines) => {
  if (!lines) return [];

  const processedBundles = [];
  const formattedLines = lines
    .map((line) => {
      const bundleProductsId = line.properties?._bundleProductsId;
      // If not a bundle item return the default line object
      if (!bundleProductsId) return line;
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId)) return null;

      // Get bundle items
      const bundleItems = lines
        .filter((l) => {
          const currentId = l.properties?._bundleProductsId;
          if (currentId === bundleProductsId) return l;
          return null;
        })
        .map((l) => l);

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(firstItem.properties._bundleData);

      // Return bundle line item
      return {
        merchandise: bundleData.merchandise,
        cost: bundleData.cost,
        quantity: firstItem.quantity,
        children: bundleItems,
      };
    }, [])
    .filter((i) => i);

  return formattedLines.filter((x) => x);
};

export const formatImage = (image) => ({
  ...image,
});

export const formatMedia = (media) => ({
  ...media,
});

const metaobjectFieldsToObject = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.key] = field;
    return acc;
  }, {});

const formatAccordionData = (product) => {
  const accordionRefs = product.accordions?.references?.edges;
  const iconRefs = product.productIcons?.references?.edges;

  if (!accordionRefs && !iconRefs) return null;

  const accordions = (accordionRefs || []).map(({ node }) => {
    const fields = metaobjectFieldsToObject(node.fields);
    let copy = null;
    let modalCopy = null;
    try { copy = fields.copy?.value ? JSON.parse(fields.copy.value) : null; } catch (e) { /* invalid rich text */ }
    try { modalCopy = fields.modal_copy?.value ? JSON.parse(fields.modal_copy.value) : null; }
    catch (e) { /* invalid rich text */ }

    return {
      title: fields.title?.value || '',
      copy,
      modal_cta_text: fields.modal_cta_text?.value || '',
      modal_copy: modalCopy,
      open_by_default: fields.open_by_default?.value === 'true',
    };
  });

  const productIcons = (iconRefs || []).map(({ node }) => {
    const fields = metaobjectFieldsToObject(node.fields);
    const imageUrl = fields.product_icon_image?.reference?.image?.url;
    const altText = fields.product_icon_image?.reference?.image?.altText || '';
    return {
      title: fields.product_icon_title?.value || '',
      shortDescription: fields.short_description?.value || '',
      image: imageUrl ? `<img src="${imageUrl}" alt="${altText}">` : null,
    };
  });

  return { accordions, productIcons };
};

export const formatProduct = (product) => ({
  ...product,
  variants: edgesToNodes(product.variants, 'variant'),
  images: edgesToNodes(product.images, 'image'),
  media: edgesToNodes(product.media, 'media'),
  hasOnlyDefaultVariant:
    product.options.length === 1 &&
    product.options[0].values[0] === 'Default Title',
  subtitle: product.subtitle?.value,
  swatchColor: product.swatchColor?.value,
  benefits: product.benefits?.value,
  feelsLike: product.feelsLike?.value,
  smellsLike: product.smellsLike?.value,
  cartDrawerItemBodyPromoBadge: product.cartDrawerItemBodyPromoBadge?.value,
  promo: {
    promo_message_content_01: product.promoContent1?.value
      ? richTextToHTML(product.promoContent1.value)
      : null,
    promo_message_content_02: product.promoContent2?.value
      ? richTextToHTML(product.promoContent2.value)
      : null,
    promo_message_title_01: product.promoTitle1?.value,
    promo_message_title_02: product.promoTitle2?.value,
  },
  singleBundleItems: product.singleBundleItems
    ? product.singleBundleItems.references.edges
        .map((el) =>
          el.node.fields
            .filter((field) => field.key === 'title')
            .map((i) => i.value),
        )
        .flat()
    : null,
  accordionData: formatAccordionData(product),
});

export const formatVariant = (variant) => ({
  ...variant,
});

export const formatMoney = (
  { amount, currencyCode = 'USD' },
  cents = false,
) => {
  let amt = amount;
  if (Number.isNaN(amt))
    // eslint-disable-next-line no-console
    return console.error('utils/format-money.js: No amount value passed.');

  if (cents) amt /= 100;

  const formatterOptions = {
    style: 'currency',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  };

  if (currencyCode !== '') {
    formatterOptions.currency = currencyCode;
  }

  const locale = getCurrentLocale() ?? 'en-US';
  const formatter = new Intl.NumberFormat(locale, formatterOptions);

  return formatter.format(amt);
};

/**
 * Takes an array of products and merges them into a single product
 * @param {array} products
 * @returns fully merged product object
 */
export const mergeProduct = (prods) => {
  const products = Array.from(prods);
  let swatchArr = [];
  const firstProduct = products.pop();
  const customSort = (a, b) =>
    Number(a.match(/(\d+)/g)[0]) - Number(b.match(/(\d+)/g)[0]);

  return products.reduce((a, current) => {
    const options = a.options.map((o) => {
      const matchingOption = current.options.find((co) => co.name === o.name);
      swatchArr = [...swatchArr, current.swatchColor];
      let sizeArr = o.values.concat(matchingOption?.values).filter(uniq);
      if (o.name?.toLowerCase() === 'size' && sizeArr.length > 1) {
        sizeArr = sizeArr.sort(customSort);
      }
      if (matchingOption) {
        return {
          ...o,
          values: sizeArr,
          swatches: [firstProduct.swatchColor, ...swatchArr],
        };
      }
      return o;
    });
    return {
      ...current,
      options,
      variants: current.variants.concat(a.variants),
    };
  }, firstProduct);
};

/**
 * Takes an array of products and sorts them into arrays based on their group tag.
 * Used for grouping products within a bundle.
 * @param {array} products
 * @returns an sorted/merged array product objects
 */
export const mergeProducts = (products) => {
  // Creates an object of arrays of sorted products
  const sortedProducts = products.reduce(
    (a, current) => {
      const group = current.tags?.find((tag) => tag.startsWith('group::'));
      const sorted = { ...a };
      if (group) {
        sorted[group] = sorted[group] ?? [];
        sorted[group].push(current);
      } else {
        sorted.ungrouped.push(current);
      }
      return sorted;
    },
    { ungrouped: [] },
  );

  // Merges/concats arrays of sorted products
  const groupedProducts = Object.entries(sortedProducts).reduce(
    (a, [key, value]) => {
      if (key === 'ungrouped') {
        a.push(...value);
      } else {
        a.push(mergeProduct(value));
      }
      return a;
    },
    [],
  );
  return groupedProducts;
};

export function richTextToHTML(content) {
  const parsed = JSON.parse(content);
  let html = '<p>';
  parsed.children.forEach((node) => {
    switch (node.type) {
      case 'heading':
        html += `<h${node.level}>${node.children[0].value}</h${node.level}>`;
        break;
      case 'list':
        html += `<${node.listType === 'unordered' ? 'ul' : 'ol'}>`;
        node.children.forEach((item) => {
          html += `<li>${item.children[0].value}</li>`;
        });
        html += `<${node.listType === 'unordered' ? '/ul' : '/ol'}>`;
        break;
      case 'paragraph':
        html += `<p>`;
        node.children.forEach((item) => {
          if (item.type === 'text' && item.bold) {
            html += `<strong>${item.value}</strong> `;
          } else if (item.type === 'text' && item.italic) {
            html += `<em>${item.value}</em> `;
          } else if (item.type === 'text') {
            html += `${item.value} `;
          }
          if (item.type === 'link' && item.bold) {
            html += `<a href="${item.url}" target="${item.target}">
                <strong>${item.children[0].value}</strong></a> `;
          } else if (item.type === 'link' && item.italic) {
            html += `<a href="${item.url}" target="${item.target}">
                <em>${item.children[0].value}</em></a> `;
          } else if (item.type === 'link') {
            html += `<a href="${item.url}" target="${item.target}">
                ${item.children[0].value}</a> `;
          }
        });
        html += `</p>`;
        break;
      default:
        html += '';
        break;
    }
  });
  html += `</p>`;
  return html;
}

export function getCurrentLocale() {
  const technicalDataEl = document.querySelector('[data-technical]');
  if (!technicalDataEl) return null;
  const data = JSON.parse(technicalDataEl.textContent);
  return `${data.localization.locale}`;
}
